import type { ReactNode } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { SearchLg } from '@untitledui/icons/SearchLg';
import { Users01 } from '@untitledui/icons/Users01';

import { LinkButton } from '@/components/base/buttons/button';
import { FeaturedIcon } from '@/components/foundations/featured-icon/featured-icon';
import { BASE_PATH } from '@/constants/config';

import { loginDestinationForRoles } from './login';

export const Route = createFileRoute('/signup')({
  beforeLoad: ({ context }) => {
    // Same policy as `/login`: an already-signed-in visitor goes to their own
    // side of the marketplace. Shared so the two cannot drift.
    if (context.user) {
      throw redirect({ to: loginDestinationForRoles(context.user.roles) });
    }
  },
  component: SignupChoicePage,
});

/**
 * Asks which side of the marketplace you are joining, then hands off to that
 * side's signup form.
 *
 * Centred rather than using `AuthLayout`: this page has no form to balance a
 * marketing panel against, and the choice is the whole content. Researcher
 * first, and the only filled button — that is the side we are recruiting for,
 * and a participant arriving here has almost always followed a study link and
 * knows what they came for.
 */
function SignupChoicePage() {
  const { t } = useLingui();

  return (
    <div className="flex min-h-screen flex-col items-center bg-secondary_subtle px-4 py-10 sm:py-16">
      <img
        src={`${BASE_PATH}/logo-dark.svg`}
        alt="Respondent"
        className="h-8"
      />

      <h1 className="mt-10 text-center font-heading text-display-md font-semibold text-primary sm:mt-16">
        <Trans>Join Respondent</Trans>
      </h1>

      <div className="mt-10 grid w-full max-w-4xl gap-6 sm:mt-14 md:grid-cols-2">
        <SignupOption
          icon={
            <FeaturedIcon
              icon={SearchLg}
              color="brand"
              theme="light"
              size="lg"
            />
          }
          title={<Trans>Sign up as a researcher</Trans>}
          description={
            <Trans>Find the right participants and run better research.</Trans>
          }
          action={
            <LinkButton
              to="/researchers/signup"
              color="primary"
              size="xl"
              className="w-full justify-center"
            >
              <Trans>Start recruiting</Trans>
            </LinkButton>
          }
        />
        <SignupOption
          icon={
            <FeaturedIcon
              icon={Users01}
              color="gray"
              theme="modern"
              size="lg"
            />
          }
          title={<Trans>Sign up as a participant</Trans>}
          description={
            <Trans>
              Share your expertise and get paid for research opportunities.
            </Trans>
          }
          action={
            <LinkButton
              to="/participants/signup"
              color="secondary"
              size="xl"
              className="w-full justify-center"
            >
              <Trans>Browse studies</Trans>
            </LinkButton>
          }
        />
      </div>

      <p className="mt-10 text-md text-tertiary sm:mt-14">
        <Trans>Already have an account?</Trans>{' '}
        <Link
          to="/login"
          className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
          aria-label={t`Log in to your account`}
        >
          <Trans>Log in</Trans>
        </Link>
      </p>
    </div>
  );
}

/**
 * The whole card is not a link: it carries a button, and nesting an
 * interactive control inside a link is both invalid and a real nuisance for
 * keyboard and screen-reader users. One target per card, in the button.
 */
function SignupOption({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  action: ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-secondary bg-primary p-6 sm:p-8">
      {icon}
      <h2 className="mt-6 font-heading text-display-xs font-semibold text-primary">
        {title}
      </h2>
      <p className="mt-2 text-md text-secondary">{description}</p>
      <div className="mt-8 flex flex-1 items-end sm:mt-10">{action}</div>
    </div>
  );
}
